import {
  css as deprecatedCss,
  view as deprecatedView,
} from '@sail/ui/deprecated';
import {view, Icon, tokens} from '@sail/ui';
import {animate as animateDeprecated} from '@sail/engine/deprecated';
import {AnimatePresence, animate} from '@sail/engine';
import type {IconAsset} from '@sail/icons/types';
import {call, chat, email, external, send} from '@sail/icons/react/Icon';
import {useRef} from 'react';
import {flushSync} from 'react-dom';
import {
  useNewUniversalHelpDesign,
  useUniversalHelpDotComDesign,
} from '../../FeatureFlagsProvider';
import {TextBubble} from './TextBubble';
import {dotComColors} from '../../theme/dotCom';
import {useTheme} from '../../ThemeProvider';
import {ANIMATION_DURATION} from '../../animation';

export interface Option {
  value: string;
  label: string;
  onPress?: () => void;
  icon?: IconAsset;
  externalUrl?: string;
}

// Keep up to date with Opus::SupportConversations::Utilities::Icons
// Please alphabetize
const unicodeToSailIconMap: Record<string, IconAsset> = {
  '📞': call,
  '💬': chat,
  '📧': email,
  '➢': send,
};

const parseIconIfPresent = (option: Option) => {
  if (option.icon) {
    return {label: option.label, icon: option.icon};
  }

  let {label} = option;
  let icon: IconAsset | undefined;

  // eslint-disable-next-line no-restricted-syntax
  for (const key of Object.keys(unicodeToSailIconMap)) {
    if (label.startsWith(key)) {
      icon = unicodeToSailIconMap[key];
      label = label.slice(key.length).trim();
      break;
    }
  }

  return {label, icon};
};

type Props = {
  recommended?: boolean;
  selected?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  animated?: boolean;
  option: Option;
  onPress?: () => void;
  index: number;
};

const ChatButtonOptionOld = ({
  selected = false,
  disabled = false,
  animated = false,
  option,
  onPress,
}: Omit<Props, 'index' | 'hidden' | 'recommended'>): JSX.Element => {
  const bg = selected ? 'neutral600' : 'white';
  const border = selected ? 'neutral600' : 'primary';
  const color = selected ? deprecatedCss.value('white') : 'primary';

  // selected options won't be hoverable, so these values are only for unselected buttons
  // at the moment, border & text color remains primary on hover, the bg will slightly darken
  const bgHover = 'neutral100';
  const borderHover = 'primary';
  const colorHover = 'primary';

  const {label, icon} = parseIconIfPresent(option);

  return (
    <deprecatedView.button
      // 'data-text' is used across entire universal help synthetic test suite, kindly refrain from removing this attribute
      data-text={label}
      data-testid="chat-option"
      disabled={disabled}
      css={{
        border: deprecatedCss.value('solid'),
        borderWidth: deprecatedCss.value('1px'),
        borderRadius: 'medium',
        borderColor: deprecatedCss.value(`$color.${border}`),
        background: deprecatedCss.value(`$color.${bg}`),
        paddingX: 'medium',
        paddingY: 'small',
        whiteSpace: deprecatedCss.value('pre-line'),
        overflow: deprecatedCss.value('hidden'),
        overflowWrap: deprecatedCss.value('break-word'),
        color,
        width: deprecatedCss.value('fit-content'),
        textAlign: deprecatedCss.value('start'),
        '&:hover':
          selected || disabled
            ? {}
            : {
                background: deprecatedCss.value(`$color.${bgHover}`),
                borderColor: deprecatedCss.value(`$color.${borderHover}`),
                color: colorHover,
              },
      }}
      onClick={onPress}
      uses={
        animated
          ? [
              animateDeprecated.inout({
                duration: ANIMATION_DURATION,
                opacity: 0,
                easing: 'ease-in-out',
              }),
            ]
          : []
      }
    >
      <view.div css={{stack: 'x', gap: 'small', alignY: 'center'}}>
        {icon && !selected ? (
          <Icon
            icon={icon}
            size="xsmall"
            data-testid="chat-button-option-icon"
          />
        ) : null}
        {label}
      </view.div>
    </deprecatedView.button>
  );
};

const ChatButtonOptionNext = ({
  recommended = false,
  selected = false,
  disabled = false,
  animated = false,
  option,
  onPress,
}: Omit<Props, 'index' | 'hidden'>): JSX.Element => {
  const {label, icon} = parseIconIfPresent(option);
  const theme = useTheme();

  if (selected) {
    return (
      <view.div css={{marginY: -2}}>
        <TextBubble body={label} messageType="user" />
      </view.div>
    );
  }

  const isExternalLink = Boolean(option.externalUrl);
  // This is done so that we are able to open the externalLink in a new tab using href property.
  const Element = isExternalLink ? view.a : view.button;

  const sharedCss = {
    display: 'flex',
    alignItems: 'center',
    border: 'solid',
    borderWidth: theme.buttonBorderWidth,
    borderRadius: 'xlarge',
    borderColor: recommended ? tokens.color.brand[400] : 'transparent',
    paddingX: 'space.150',
    paddingY: 'space.100',
    font: 'label.small',
    textColor: recommended ? theme.buttonHoverColor : 'text',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    backgroundImage: theme.buttonBackgroundImage,
    transition: 'border-color 0.4s ease',
    width: 'fit-content',
    whiteSpace: 'pre-line',
    overflow: 'hidden',
    overflowWrap: 'break-word',
    textAlign: 'start',
    ...(isExternalLink && {textDecoration: 'none'}),
    ':hover': {
      borderColor: theme.buttonHoverColor,
      ...(isExternalLink && {textDecoration: 'none'}),
    },
  } as const;

  const sharedUses = [
    animated &&
      animate.in({
        duration: ANIMATION_DURATION,
        opacity: 0,
        easing: 'ease-in-out',
      }),
  ];

  const iconCss = {
    minWidth: 'space.150',
    marginRight: 'small',
    ...(recommended && {color: theme.buttonHoverColor}),
  };

  const elementProps = isExternalLink
    ? {
        href: option.externalUrl,
        target: '_blank',
        rel: 'noopener noreferrer',
      }
    : {
        disabled,
        onClick: onPress,
      };

  return (
    <Element
      // 'data-text' is used across entire universal help synthetic test suite, kindly refrain from removing this attribute
      data-text={label}
      className={`chat-button-option${selected ? ' selected' : ''}`}
      data-testid="chat-option"
      css={sharedCss}
      uses={sharedUses}
      {...elementProps}
    >
      {icon ? (
        <Icon
          icon={icon}
          size="xsmall"
          data-testid="chat-button-option-icon"
          css={iconCss}
        />
      ) : null}
      {label}
      {isExternalLink ? (
        <Icon
          icon={external}
          size="xsmall"
          css={{
            minWidth: 'space.150',
            marginLeft: 'small',
            ...(recommended && {color: theme.buttonHoverColor}),
          }}
        />
      ) : null}
    </Element>
  );
};

const ChatButtonOptionDotCom = ({
  selected = false,
  disabled = false,
  option,
  onPress,
  index,
}: Omit<Props, 'animated' | 'hidden' | 'recommended'>): JSX.Element => {
  const ref = useRef<HTMLButtonElement>(null);
  const theme = useTheme();

  const handleClick = async () => {
    if (!ref.current || !ref.current.parentElement) {
      return;
    }

    const easing: KeyframeAnimationOptions = {
      duration: 350,
      easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
      fill: 'forwards',
    };
    const {parentElement} = ref.current;
    const selectedBtnHeight = ref.current.getBoundingClientRect().height;

    const collapseHeightAnimation = parentElement.animate(
      [
        {height: `${parentElement.offsetHeight}px`},
        {height: `${selectedBtnHeight}px`},
      ],
      easing,
    );

    const allButtons = parentElement.querySelectorAll('.chat-button-option');
    const nonSelectedButtonsFadeAnimation = Array.from(allButtons)
      .filter((buttonToFade) => buttonToFade !== ref.current)
      .map((buttonToFade) => {
        return buttonToFade.animate(
          [
            {
              opacity: 1,
              transform: 'translateY(0px)',
            },
            {
              opacity: 0,
              transform: `translateY(-${selectedBtnHeight}px)`,
            },
          ],
          easing,
        ).finished;
      });

    const selectedBtnPositionAnimation = ref.current.animate(
      [
        {
          transform: 'translateY(0px)',
        },
        {
          transform: `translateY(-${ref.current.offsetTop}px)`,
          borderColor: theme.linkColor,
          background: theme.linkColor,
          color: dotComColors.brand,
          zIndex: 10,
        },
      ],
      easing,
    );

    const animations = await Promise.all([
      collapseHeightAnimation.finished,
      selectedBtnPositionAnimation.finished,
      ...nonSelectedButtonsFadeAnimation,
    ]);
    animations.forEach((animation) => animation.cancel());

    // This is necessary to ensure that react's state is updated *immediately* after all animations have finished.
    flushSync(() => onPress && onPress());
  };

  const {label} = parseIconIfPresent(option);

  return (
    <view.button
      ref={ref}
      // 'data-text' is used across entire universal help synthetic test suite, kindly refrain from removing this attribute
      data-text={label}
      className={`chat-button-option${selected ? ' selected' : ''}`}
      data-testid="chat-option"
      disabled={disabled}
      css={{
        transformOrigin: 'right bottom',
        border: '1px solid',
        borderColor: selected ? theme.linkColor : dotComColors.brand300,
        borderRadius: 'medium',
        background: selected ? theme.linkColor : dotComColors.brand,
        paddingX: '14px',
        paddingY: 'small',
        whiteSpace: 'pre-line',
        overflow: 'hidden',
        overflowWrap: 'break-word',
        color: selected ? dotComColors.brand : dotComColors.brand600,
        width: 'fit-content',
        maxHeight: 'fit-content',
        textAlign: 'start',
        ...(!selected && {
          ':hover': {
            color: theme.linkColor,
            borderColor: '#c4ccd8',
            boxShadow:
              '0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.06), 0 0.125rem 0.25rem 0 rgba(0, 0, 0, 0.1)',
          },
        }),
      }}
      onClick={handleClick}
      uses={[
        animate.in({
          duration: 250,
          opacity: 0,
          delay: index * 100,
          transform: 'translateX(20px)',
          easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
          disabled: selected,
        }),
      ]}
    >
      {label}
    </view.button>
  );
};

export const ChatButtonOption = ({
  hidden = false,
  ...props
}: Props): JSX.Element => {
  const useNewDesign = useNewUniversalHelpDesign();
  const useDotComDesign = useUniversalHelpDotComDesign();

  // eslint-disable-next-line no-nested-ternary
  const Component = useNewDesign
    ? ChatButtonOptionNext
    : useDotComDesign
    ? ChatButtonOptionDotCom
    : ChatButtonOptionOld;

  return (
    <AnimatePresence>
      {hidden ? null : <Component {...props} />}
    </AnimatePresence>
  );
};
